import { ReactNode, useEffect, useId } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock } from 'lucide-react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { CopyButton, Header, TelInput } from '@components/patterns';
import {
  Button,
  DialogState,
  Drawer,
  FormField,
  Input,
  InputGroup,
  Separator,
  Tooltip,
} from '@components/primitives';
import { toast } from '@vault';

import { AppObject } from '@api/types';
import {
  asvRegistrationDefaultValues,
  AsvRegistrationSchema,
  asvRegistrationSchema,
} from '@components/features/Admin/AsvRegistrationDrawer/schema';
import { formatAsvScansIpCost } from '@components/features/AsvScans/constants';
import {
  useAsvConfigQuery,
  useCreateAsvConfigMutation,
  useUpdateAsvCompanyMutation,
  useUpdateAsvConfigMutation,
} from '@components/features/AsvScans/queries';
import {
  CreateAsvConfigRequest,
  UpdateAsvCompanyRequest,
} from '@components/features/AsvScans/types';
import { AddressInput } from '@components/features/Payments/Merchants/CreateMerchantDialog/AddressInput';

import css from './styles.module.css';

interface AsvRegistrationFormProps {
  app: AppObject;
  onCancel(): void;
  onSuccess(): void;
}
function AsvRegistrationForm({
  app,
  onCancel,
  onSuccess,
}: AsvRegistrationFormProps) {
  const id = useId();

  const { isLoading, data: asvConfig } = useAsvConfigQuery(
    app.teamUuid,
    app.uuid
  );
  const methods = useForm<AsvRegistrationSchema>({
    resolver: zodResolver(asvRegistrationSchema),
    defaultValues: asvRegistrationDefaultValues,
    mode: 'onTouched',
  });

  const { errors, isValid, isSubmitting, isDirty } = methods.formState;

  useEffect(() => {
    if (!isSubmitting && asvConfig) {
      const { organization } = asvConfig;
      const lines = organization.details.address?.split('\n') ?? [];
      methods.reset({
        company: {
          name: organization.name,
          website: organization.details.website ?? undefined,
          address: {
            line1: lines.at(0) ?? '',
            line2: lines.slice(1).join(', '),
            city: organization.details.city ?? undefined,
            country: organization.details.country ?? undefined,
            state: organization.details.state ?? undefined,
            postalCode: organization.details.zipcode ?? undefined,
          },
        },
        owner: {
          name: organization.details.contactName ?? undefined,
          title: organization.details.contactTitle ?? undefined,
          email: organization.details.contactEmail ?? undefined,
          phone: organization.details.contactPhone ?? undefined,
        },
        allocatedIps: asvConfig.asvConfig.allocatedIps,
      });
    }
  }, [isSubmitting, asvConfig]);

  const { mutateAsync: createAsvConfig } = useCreateAsvConfigMutation(
    app.teamUuid,
    app.uuid
  );
  const { mutateAsync: updateAsvCompany } = useUpdateAsvCompanyMutation(
    app.teamUuid,
    app.uuid
  );
  const { mutateAsync: updateAsvConfig } = useUpdateAsvConfigMutation(
    app.teamUuid,
    app.uuid
  );

  const submit = methods.handleSubmit(async (data) => {
    if (!isDirty) {
      onCancel();
      return;
    }

    if (!asvConfig) {
      const createRequest: CreateAsvConfigRequest = {
        config: {
          name: data.company.name,
        },
        organization: {
          companyName: data.company.name,
          name: data.company.name,
          details: {
            address: [data.company.address.line1, data.company.address.line2]
              .filter(Boolean)
              .join(', '),
            city: data.company.address.city,
            zipcode: data.company.address.postalCode,
            country: data.company.address.country,
            phone: data.owner.phone,
            website: data.company.website,
            email: data.owner.email,
            contactName: data.owner.name,
            contactTitle: data.owner.title,
            contactEmail: data.owner.email,
            contactPhone: data.owner.phone,
          },
        },
        user: {
          email: data.owner.email,
        },
      };
      if (data.company.address.state) {
        createRequest.organization.details.state = data.company.address.state;
      }
      await createAsvConfig(createRequest, {
        onError() {
          toast.error('Failed to create ASV config');
        },
      });
    } else {
      const updateRequest: UpdateAsvCompanyRequest = {
        address: [data.company.address.line1, data.company.address.line2]
          .filter(Boolean)
          .join(', '),
        city: data.company.address.city,
        country: data.company.address.country,
        zipcode: data.company.address.postalCode,
        website: data.company.website,
        contactName: data.owner.name,
        contactTitle: data.owner.title,
        contactEmail: data.owner.email,
        contactPhone: data.owner.phone,
      };
      if (data.company.address.state) {
        updateRequest.state = data.company.address.state;
      }
      await updateAsvCompany(updateRequest, {
        onError() {
          toast.error('Failed to update ASV config');
        },
      });
    }

    await updateAsvConfig(
      { ipAddressCount: data.allocatedIps },
      {
        onError() {
          toast.error('Failed to update ASV config');
        },
      }
    );

    toast.success('ASV configured');
    onSuccess();
  });

  return (
    <FormProvider {...methods}>
      <form id={id} className={css.form} onSubmit={submit}>
        <Drawer.Description className={css.description}>
          Configure ASV scans for the app <strong>{app.name}</strong>
        </Drawer.Description>

        <Separator className={css.separator} />

        <section className={css.section}>
          <Header size="sm" hasBorder={false}>
            <Header.Title>Company</Header.Title>
            <p>Ask the team about their legal entity</p>
          </Header>

          {asvConfig && (
            <FormField
              label="Clone ID"
              helperText="The internal Clone organization ID"
            >
              <InputGroup>
                <InputGroup.Input readOnly value={asvConfig.organization.id} />
                <InputGroup.Addon align="inline-end">
                  <CopyButton
                    value={asvConfig.organization.id}
                    title="Copy ID"
                    size="2xs"
                    ghost
                  />
                </InputGroup.Addon>
              </InputGroup>
            </FormField>
          )}

          <FormField label="Name" error={errors.company?.name?.message}>
            <InputGroup>
              <InputGroup.Input
                {...methods.register('company.name')}
                placeholder="Acme Inc"
                data-1p-ignore
                readOnly={isLoading || !!asvConfig}
              />
              {asvConfig && (
                <InputGroup.Addon align="inline-end">
                  <Tooltip content="Read-only" side="top">
                    <Lock />
                  </Tooltip>
                </InputGroup.Addon>
              )}
            </InputGroup>
          </FormField>

          <FormField label="Website" error={errors.company?.website?.message}>
            <Input
              {...methods.register('company.website', {
                disabled: isLoading,
              })}
              placeholder="acme.com"
              data-1p-ignore
            />
          </FormField>

          <FormField label="Address" error={errors.company?.address?.message}>
            <AddressInput name="company.address" disabled={isLoading} />
          </FormField>
        </section>

        <Separator className={css.separator} />

        <section className={css.section}>
          <Header size="sm" hasBorder={false}>
            <Header.Title>Owner</Header.Title>
            <p>Ask the team who is in charge of compliance</p>
          </Header>

          <FormField label="Name" error={errors.owner?.name?.message}>
            <Input
              {...methods.register('owner.name', { disabled: isLoading })}
              placeholder="Johnny Appleseed"
            />
          </FormField>

          <FormField label="Title" error={errors.owner?.title?.message}>
            <Input
              {...methods.register('owner.title', { disabled: isLoading })}
              placeholder="Head of Compliance"
            />
          </FormField>

          <FormField label="Email" error={errors.owner?.email?.message}>
            <Input
              {...methods.register('owner.email', { disabled: isLoading })}
              type="email"
              placeholder="johnny@acme.com"
            />
          </FormField>

          <FormField
            label="Phone"
            error={errors.owner?.phone?.message}
            helperText="A number is required for compliance reasons (but we'll never call you)"
          >
            <Controller
              control={methods.control}
              name="owner.phone"
              render={({ field }) => (
                <TelInput
                  {...field}
                  placeholder="1234567890"
                  disabled={field.disabled || isLoading}
                />
              )}
            />
          </FormField>
        </section>

        <Separator className={css.separator} />

        <section className={css.section}>
          <Header size="sm" hasBorder={false}>
            <Header.Title>Limits</Header.Title>
            <p>Ask the team how many IP addresses they need to scan</p>
          </Header>

          <FormField
            label="IP address count"
            helperText={`Each IP address will cost ${formatAsvScansIpCost()}`}
            error={errors.allocatedIps?.message}
          >
            <Input
              {...methods.register('allocatedIps', { disabled: isLoading })}
              type="number"
              placeholder="100"
            />
          </FormField>
        </section>
      </form>

      <Drawer.Footer className={css.footer}>
        <Button type="button" ghost onClick={onCancel}>
          Cancel
        </Button>
        <Button
          form={id}
          type="submit"
          variant="primary"
          disabled={!isValid}
          loading={isSubmitting}
        >
          {asvConfig ? 'Save' : 'Register'}
        </Button>
      </Drawer.Footer>
    </FormProvider>
  );
}

export interface AsvRegistrationDrawerProps {
  app: AppObject;
  state: DialogState;
  trigger?: ReactNode;
}

export function AsvRegistrationDrawer({
  app,
  state,
  trigger,
}: AsvRegistrationDrawerProps) {
  return (
    <Drawer state={state} trigger={trigger}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Configure ASV</Drawer.Title>
          <Drawer.Close />
        </Drawer.Header>
        <AsvRegistrationForm
          app={app}
          onCancel={state.close}
          onSuccess={state.close}
        />
      </Drawer.Content>
    </Drawer>
  );
}
